import React, { useState, useEffect } from 'react';
import { Grid, Loader, Message, Icon } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../app/stores/store';
import CourtFilters from './CourtFilters';
import CourtList from './CourtList';

export default observer(function CourtDashboard() {
    const { t } = useTranslation();
    const { courtStore, userStore } = useStore();
    const [hasSearched, setHasSearched] = useState(false);

    const userLat = userStore.user?.latitude ?? 0;
    const userLng = userStore.user?.longitude ?? 0;
    const isUserLocationSet = userStore.user && (userLat !== 0 || userLng !== 0);

    const handleSearch = (replace: boolean) => {
        if (!isUserLocationSet) return;
        setHasSearched(true);
        courtStore.loadCourts(userLat, userLng, replace);
    };

    // Auto-trigger the default search when the tab is first opened and the
    // user's location is available — mirrors the behaviour of the Games tab
    // where results appear immediately without requiring a button press.
    useEffect(() => {
        if (isUserLocationSet && !hasSearched) {
            handleSearch(true);
        }
    }, [isUserLocationSet]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Grid padded style={{ margin: 0, width: '100%' }}>
            {/* Filters row */}
            <Grid.Row>
                <Grid.Column width={16}>
                    <CourtFilters onSearch={handleSearch} />
                </Grid.Column>
            </Grid.Row>

            {/* Results row */}
            <Grid.Row>
                <Grid.Column width={16}>
                    {!isUserLocationSet && (
                        <Message warning icon>
                            <Icon name='location arrow' />
                            <Message.Content>
                                <Message.Header>{t('courts.locationRequired.title')}</Message.Header>
                                {t('courts.locationRequired.message')}
                            </Message.Content>
                        </Message>
                    )}

                    {isUserLocationSet && courtStore.loadingInitial ? (
                        // Placeholder cards while loading
                        <>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{
                                    background: '#f9fafb',
                                    borderRadius: '4px',
                                    height: '120px',
                                    marginBottom: '10px',
                                    animation: 'pulse 1.5s infinite'
                                }} />
                            ))}
                            <style>{`
                                @keyframes pulse {
                                    0%   { opacity: 1; }
                                    50%  { opacity: 0.4; }
                                    100% { opacity: 1; }
                                }
                            `}</style>
                        </>
                    ) : (
                        isUserLocationSet && hasSearched && (
                            <CourtList
                                userLat={userLat}
                                userLng={userLng}
                            />
                        )
                    )}
                </Grid.Column>
            </Grid.Row>

            {/* Infinite scroll loader */}
            <Grid.Row>
                <Grid.Column width={16}>
                    <Loader active={courtStore.loadingCourts && !courtStore.loadingInitial} />
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
});